import { useMemo, useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Footer from '@/components/Footer'
import FieldErrorIcon from '@/components/FieldErrorIcon'
import SchoolInfoCard from '@/components/SchoolInfoCard'
import ProjectInfoCard from '@/components/ProjectInfoCard'
import ScheduleTimeCard from '@/components/ScheduleTimeCard'
import ContactNotesCard from '@/components/ContactNotesCard'
import { useToast } from '@/context/ToastContext'
import { useTownContext } from '@/context/TownContext'
import { useProjectFormContext } from '@/context/ProjectFormContext'
import { CompanySurveyService } from '@common/services/company_survey_service'
import { priorContactVisitPlanLabel } from '@common/utils/prior-contact-visit-plan'
import { formatJapanesePostalCodeDisplay } from '@common/utils/postal-code'
import { DotLoader } from 'react-spinners'
import { syncSentryTownTags } from '@/utils/sentry-town-context'

function Login() {
  const { schoolId, projectId } = useParams<{ schoolId: string; projectId: string }>()
  const [managementId, setManagementId] = useState('')
  const [fieldError, setFieldError] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const [showCloseReasonPopup, setShowCloseReasonPopup] = useState(true)
  const navigate = useNavigate()
  const { showErrorMessage } = useToast()
  const { displayInfo } = useTownContext()
  const { form, loading: formLoading, error: formError } = useProjectFormContext()

  useEffect(() => {
    syncSentryTownTags({
      schoolName: displayInfo?.schoolName ?? form?.schoolName,
      projectName: displayInfo?.projectName ?? form?.projectName,
    })
    return () => syncSentryTownTags(null)
  }, [
    displayInfo?.schoolName,
    displayInfo?.projectName,
    form?.schoolName,
    form?.projectName,
  ])

  const isAccepting = form?.closed === false
  const latestDeadline = form?.deadline ?? null
  const formatDeadline = (d: Date) =>
    `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`

  const displayValue = (value?: string) => (value && value.trim().length > 0 ? value : '—')
  const displayNumber = (value?: number, suffix: string = '') =>
    value != null ? `${value}${suffix}` : '—'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setFieldError(null)
    if (!schoolId || !projectId) return
    const mid = managementId?.trim() || ''
    if (!mid) {
      const msg = '事業所管理IDを入力してください'
      setFieldError(msg)
      showErrorMessage(msg)
      return
    }
    setIsChecking(true)
    try {
      const surveyService = new CompanySurveyService()
      const sendLists = await surveyService.getSendListsForProject(schoolId, projectId)
      const sendListWithTarget = sendLists
        .filter((sl) => sl.sendTargets?.some((t) => t.managementId === mid))
        .sort((a, b) => b.created_at.getTime() - a.created_at.getTime())[0]
      const target = sendListWithTarget?.sendTargets?.find((t) => t.managementId === mid)
      if (!target) {
        const msg = 'この事業所管理IDは郵送対象に含まれていません。'
        setFieldError(msg)
        showErrorMessage(msg)
        return
      }
      navigate(`/response/${schoolId}/${projectId}/${mid}`)
    } catch (err) {
      console.error('郵送対象チェックエラー:', err)
      const msg = '確認中にエラーが発生しました。しばらく経ってからお試しください。'
      setFieldError(msg)
      showErrorMessage(msg)
    } finally {
      setIsChecking(false)
    }
  }

  const schoolInfo = useMemo(
    () => ({
      schoolName: displayValue(displayInfo?.schoolName ?? form?.schoolName),
      principal: displayValue(displayInfo?.schoolPrincipal ?? form?.schoolPrincipal),
      postalCode: displayValue(
        formatJapanesePostalCodeDisplay(displayInfo?.schoolPostalCode ?? form?.schoolPostalCode)
      ),
      address: displayValue(displayInfo?.schoolAddress ?? form?.schoolAddress),
      phone: displayValue(displayInfo?.schoolPhone ?? form?.schoolPhone),
      email: displayValue(displayInfo?.schoolEmail ?? form?.schoolEmail),
      contactPersonName: displayValue(displayInfo?.projectContactPersonName ?? form?.projectContactPersonName),
    }),
    [displayInfo, form]
  )

  const projectInfo = useMemo(
    () => ({
      projectNumber: displayValue(displayInfo?.projectNumber ?? form?.projectNumber),
      projectName: displayValue(displayInfo?.projectName ?? form?.projectName),
      description: displayValue(displayInfo?.projectDescription ?? form?.projectDescription ?? form?.projectName),
      startDate: displayValue(displayInfo?.projectStartDate ?? form?.projectStartDate),
      endDate: displayValue(displayInfo?.projectEndDate ?? form?.projectEndDate),
      startTime: displayValue(displayInfo?.projectStartTime ?? form?.projectStartTime),
      endTime: displayValue(displayInfo?.projectEndTime ?? form?.projectEndTime),
      minExperienceDays: displayNumber(displayInfo?.projectMinExperienceDays ?? form?.projectMinExperienceDays, '日'),
      minExperienceTime: displayNumber(displayInfo?.projectMinExperienceTime ?? form?.projectMinExperienceTime, '時間'),
      studentGrade: displayValue(displayInfo?.projectStudentGrade ?? form?.projectStudentGrade),
      studentCount: displayNumber(displayInfo?.participantCount ?? form?.participantCount, '名'),
      minParticipantsPerCompany: displayNumber(displayInfo?.projectMinParticipantsPerCompany ?? form?.projectMinParticipantsPerCompany, '名'),
      maxParticipantsPerCompany: displayNumber(displayInfo?.projectMaxParticipantsPerCompany ?? form?.projectMaxParticipantsPerCompany, '名'),
      lunchNotes: displayValue(displayInfo?.projectLunchNotes ?? form?.projectLunchNotes),
      priorContactVisitPlanDisplay:
        priorContactVisitPlanLabel(
          displayInfo?.projectPriorContactVisitPlan ?? form?.projectPriorContactVisitPlan
        ) || '—',
      priorContactVisitScheduleDisplay: displayValue(
        displayInfo?.projectPriorContactVisitSchedule ?? form?.projectPriorContactVisitSchedule
      ),
    }),
    [displayInfo, form]
  )

  if (formLoading && !form) {
    return (
      <div className="town-page">
        <div className="town-loading">
          <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
          <div>読み込み中...</div>
        </div>
        <Footer />
      </div>
    )
  }

  if (formError) {
    return (
      <div className="town-error-page">
        <div className="town-error-page__message">{formError}</div>
        <a href="/" className="town-link">ログイン画面へ戻る</a>
        <Footer />
      </div>
    )
  }

  return (
    <div className="town-page">
      <div className="town-page__inner town-page__inner--auth">
        {/* 1行目: 学校情報・体験の情報 */}
        <div className="town-info-grid town-info-grid--auth-row2">
          <SchoolInfoCard
            schoolName={schoolInfo.schoolName}
            principal={schoolInfo.principal}
            postalCode={schoolInfo.postalCode}
            address={schoolInfo.address}
            phone={schoolInfo.phone}
            email={schoolInfo.email}
            contactPersonName={schoolInfo.contactPersonName}
          />
          <ProjectInfoCard
            projectNumber={projectInfo.projectNumber}
            projectName={projectInfo.projectName}
            description={projectInfo.description}
            studentGrade={projectInfo.studentGrade}
            participantCount={projectInfo.studentCount}
            minParticipantsPerCompany={projectInfo.minParticipantsPerCompany}
            maxParticipantsPerCompany={projectInfo.maxParticipantsPerCompany}
          />
        </div>
        {/* 2行目: 日程・時間・連絡・備考 */}
        <div className="town-info-grid town-info-grid--auth-row3">
          <ScheduleTimeCard
            startDate={projectInfo.startDate}
            endDate={projectInfo.endDate}
            startTime={projectInfo.startTime}
            endTime={projectInfo.endTime}
            minExperienceDays={projectInfo.minExperienceDays}
            minExperienceTime={projectInfo.minExperienceTime}
          />
          <ContactNotesCard
            priorContactVisitPlanDisplay={projectInfo.priorContactVisitPlanDisplay}
            priorContactVisitScheduleDisplay={projectInfo.priorContactVisitScheduleDisplay}
            lunchNotes={projectInfo.lunchNotes}
          />
        </div>

        {/* 回答ページ認証（一番下） */}
        <div className="town-login-form-wrap">
          <div className="login-card">
              <h2>受け入れ可否の回答はこちら</h2>
              {form && (
                <div className="login-card__status">
                  <div className="login-card__status-row">
                    <span className="login-card__status-label">受付状態</span>
                    <span className={`login-card__status-badge login-card__status-badge--${isAccepting ? 'open' : 'closed'}`}>
                      {isAccepting ? '受付中' : '受付停止中'}
                    </span>
                  </div>
                  {latestDeadline != null && (
                    <div className="login-card__status-row">
                      <span className="login-card__status-label">回答期限（参考）</span>
                      <span className="login-card__status-value">{formatDeadline(latestDeadline)}まで</span>
                    </div>
                  )}
                  {form.closeReason?.trim() && (
                    <p className="login-card__close-reason">{form.closeReason}</p>
                  )}
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className={`form-group ${fieldError ? 'has-error' : ''}`}>
                  <label htmlFor="managementId">事業所管理ID</label>
                  <input
                    id="managementId"
                    type="text"
                    value={managementId}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                      setManagementId(value)
                      setFieldError(null)
                    }}
                    placeholder="123456"
                    autoComplete="off"
                    maxLength={6}
                    className={fieldError ? 'form-control error' : 'form-control'}
                  />
                  {fieldError && (
                    <div className="field-error">
                      <FieldErrorIcon />
                      <span>{fieldError}</span>
                    </div>
                  )}
                  <div className="town-login-description">
                    はがきの宛名面に記載されている6桁の数字をご入力ください。
                  </div>
                </div>
                <button type="submit" className="login-button" disabled={isChecking}>
                  {isChecking ? '確認中...' : 'ログイン'}
                </button>
              </form>
            </div>
          </div>
      </div>
      {showCloseReasonPopup && form?.closeReason?.trim() && (
        <div
          className="town-response-confirm-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="town-close-reason-title"
        >
          <div className="town-response-confirm">
            <h2 id="town-close-reason-title" className="town-response-confirm__title">
              回答受付終了のお知らせ
            </h2>
            <div className="town-response-confirm__body">
              <p className="town-response-confirm__reason">{form.closeReason}</p>
            </div>
            <div className="town-response-confirm__actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setShowCloseReasonPopup(false)}
              >
                閉じる
              </button>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </div>
  )
}

export default Login

